import React from "react";
import { DataTable, ColumnDef } from "../ui/data-table";
import { GroupInfo, UserItem } from "@/interfaces/user.interface";
import { USER_TABLE_COLUMNS } from "@/constants/user-table.constants";
import { formatDate } from "@/utils/date.utils";
import GroupUsagePanel from "./group-usage-panel";

/**
 * Props for the UserTable component
 */
interface UserTableProps {
  /** Array of user items to display */
  data: UserItem[];
  /** Pagination information */
  pagination?: {
    page: number;
    page_size: number;
    total_items: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  };
  /** Current sort key */
  sortKey?: string;
  /** Current sort order */
  sortOrder?: "asc" | "desc" | null;
  /** Callback fired when sort changes */
  onSortChange?: (key: string, order: "asc" | "desc" | null) => void;
  /** Callback fired when page changes */
  onPageChange?: (page: number) => void;
  /** Callback fired when page size changes */
  onPageSizeChange?: (pageSize: number) => void;
  /** Whether table is loading */
  isLoading?: boolean;
  /** Callback fired when user edit is requested */
  onEdit?: (item: UserItem) => void;
  /** Array of selected user IDs */
  selectedUsers?: string[];
  /** Callback fired when user selection changes */
  onUserSelect?: (userId: string, selected: boolean) => void;
  /** Callback fired when select all changes */
  onSelectAll?: (selected: boolean) => void;
  /** Whether all users are selected */
  allSelected?: boolean;
}

/**
 * UserTable component displays users in a data table with sorting, pagination,
 * selection, and group usage panels. Uses DataTable component with custom
 * column definitions. Shows expandable group usage information.
 * 
 * @component
 * @param {UserTableProps} props - Component props
 * @param {UserItem[]} props.data - User items to display
 * @param {Object} [props.pagination] - Pagination info
 * @param {string} [props.sortKey] - Current sort key
 * @param {"asc" | "desc" | null} [props.sortOrder] - Current sort order
 * @param {(key: string, order: "asc" | "desc" | null) => void} [props.onSortChange] - Sort change callback
 * @param {(page: number) => void} [props.onPageChange] - Page change callback
 * @param {(pageSize: number) => void} [props.onPageSizeChange] - Page size change callback
 * @param {boolean} [props.isLoading] - Whether loading
 * @param {(item: UserItem) => void} [props.onEdit] - Edit callback
 * @param {string[]} [props.selectedUsers] - Selected user IDs
 * @param {(userId: string, selected: boolean) => void} [props.onUserSelect] - User select callback
 * @param {(selected: boolean) => void} [props.onSelectAll] - Select all callback
 * @param {boolean} [props.allSelected] - Whether all selected
 * @returns {JSX.Element} Data table with users and group usage panels
 * 
 * @example
 * ```tsx
 * <UserTable
 *   data={users}
 *   pagination={pagination}
 *   onSortChange={handleSort}
 *   onEdit={handleEdit}
 * />
 * ```
 */

/**
 * Render name and user ID cell
 */
const renderNameAndUserId = (item: UserItem): React.ReactNode => {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-foreground font-medium">{item.name}</span>
    </div>
  );
};

/**
 * Render email cell with truncation
 */
const renderEmail = (email: string): React.ReactNode => {
  const truncated = email.length > 25 ? `${email.substring(0, 25)}...` : email;
  return <span className="text-foreground">{truncated.toLocaleLowerCase()}</span>;
};

const renderBadge = (text: string, key: string = ""): React.ReactNode => {
  return (
    <span
      key={key? key : text}
      className={`
    px-2 py-1 rounded-full md:text-xs text-xxs font-medium 
    text-primary-600 border border-primary-300
  `}
    >
      {text}
    </span>
  );
};

const renderRoleItem = (roles: string[]): React.ReactNode => {
  if (!roles || roles.length === 0) {
    return <span className="text-gray-400 text-sm">—</span>;
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {roles.map((role) => renderBadge(role))}
    </div>
  );
};

const uniqueByName = (items: GroupInfo[]): GroupInfo[] => {
  const seen = new Set<string>();
  return items.filter(item => {
    const key = item.group_name.trim().toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const renderGroupItems = (originalGroups: GroupInfo[]): React.ReactNode => {
   const groups = uniqueByName(originalGroups)
   if (!groups || groups.length === 0) {
     return <span className="text-gray-400 text-sm">—</span>;
   }

   return (
     <div className="flex flex-wrap gap-1.5">
       {groups.map((group) => renderBadge(group.group_name, group.group_name + group.model_id))}
     </div>
   );
 };

/**
 * User Table Component
 *
 * Displays user data in a table format with:
 * - Name / User ID
 * - Email
 * - Role
 * - Access tier
 * - Last Login (formatted)
 */
export const UserTable: React.FC<UserTableProps> = ({
  data,
  pagination,
  sortKey,
  sortOrder,
  onSortChange,
  onPageChange,
  onPageSizeChange,
  isLoading = false
}) => {

  const columns: ColumnDef<UserItem>[] = [
    {
      ...USER_TABLE_COLUMNS.NAME,
      accessor: (row) => row,
      render: (item) => renderNameAndUserId(item),
      sortable: true,
      headerClassName: "min-w-[180px]",
    },
    {
      ...USER_TABLE_COLUMNS.EMAIL,
      accessor: (row) => row.email,
      render: (email) => renderEmail(email),
      sortable: true,
      headerClassName: "min-w-[180px]",
    },
    {
      ...USER_TABLE_COLUMNS.ROLE,
      accessor: (row) => row.roles,
      sortable: false,
      render: (roles) => renderRoleItem(roles),
      headerClassName: "min-w-[140px]",
    },
    {
      ...USER_TABLE_COLUMNS.ACCESS_TIER,
      accessor: (row) => row.groups,
      sortable: false,
      render: (groups) => renderGroupItems(groups),
      headerClassName: "min-w-[180px]",
    },
    {
      ...USER_TABLE_COLUMNS.LAST_LOGIN,
      accessor: (row) => row.lastActive,
      render: (time) => formatDate(time),
      sortable: true,
      headerClassName: "min-w-[200px]",
    }
  ];

  
  return (
    <DataTable
      data={data}
      columns={columns}
      pagination={pagination}
      sortKey={sortKey}
      sortOrder={sortOrder}
      onSortChange={onSortChange}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      isLoading={isLoading}
      emptyMessage="No users available"
      getRowKey={(row) => row.id}
      enableExpand={true}
      renderExpandedContent={(userItem) => <GroupUsagePanel userId={userItem.id}  groups={userItem.groups} />}
    />
  );
};

